'use client';

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addInsuranceToCartMutation,
  productInsuranceQuery,
  removeInsuranceFromCartMutation,
} from '@frodo/lib/services/http/client/api-insurance.client';
import { QUERY_KEYS } from '@frodo/lib/services/http/shared/queryKeys';
import { useAlertToast } from '@frodo/lib/hooks/domain/useAlertToast';
import { useStore } from '@frodo/lib/hooks/useStore';
import { getActionErrorMessage } from '@frodo/lib/utils/client';
import { centsToDecimal } from '@frodo/lib/utils/common/currencyManager';
import {
  useGa4Events,
  type InsurancePlacement,
} from '@frodo/lib/services/ga4/useGa4Events';
import type {
  ActionResponse,
  ProductInsuranceOffer,
} from '@frodo/models/types';

type UseProductInsuranceOfferOptions = {
  sku: string;
  placement: InsurancePlacement;
  enabled?: boolean;
};

type UseProductInsuranceOfferResult = {
  insuranceOffer: ProductInsuranceOffer | undefined;
  addInsurance: () => void;
  removeInsurance: () => void;
  isInsuranceActionPending: boolean;
};

export const useProductInsuranceOffer = ({
  sku,
  placement,
  enabled = true,
}: UseProductInsuranceOfferOptions): UseProductInsuranceOfferResult => {
  const queryClient = useQueryClient();
  const notifyAlertToast = useAlertToast(state => state.notify);
  const isFeatureEnabled = useStore().insuranceOffer.isEnabled;
  const { trackInsuranceAdded, trackInsuranceRemoved } = useGa4Events();

  const { data: insuranceOffer } = useQuery({
    ...productInsuranceQuery(sku),
    enabled: enabled && isFeatureEnabled,
    throwOnError: false,
    retry: false,
  });

  const syncInsuranceInCart = useCallback(
    async (productSku: string, isInsuranceInCartAfterAction: boolean) => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.cart() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userInfo() });
      queryClient.setQueryData(
        productInsuranceQuery(productSku).queryKey,
        (current: ProductInsuranceOffer | undefined) =>
          current
            ? {
                ...current,
                alreadyInCart: isInsuranceInCartAfterAction,
              }
            : {
                available: true,
                alreadyInCart: isInsuranceInCartAfterAction,
                plan: insuranceOffer?.plan ?? null,
              },
      );
    },
    [queryClient, insuranceOffer],
  );

  const handleInsuranceMutationSuccess = useCallback(
    async (
      data: ActionResponse,
      variables: { sku: string },
      isInsuranceInCartAfterAction: boolean,
    ) => {
      if (!data.success) {
        notifyAlertToast(data.messages ?? []);
        return;
      }

      if (insuranceOffer?.plan) {
        const trackingParams = {
          sku: variables.sku,
          placement,
          offerId: insuranceOffer.plan.matchId,
          insurancePrice: centsToDecimal(insuranceOffer.plan.priceAmount),
        };

        if (isInsuranceInCartAfterAction) {
          trackInsuranceAdded(trackingParams);
        } else {
          trackInsuranceRemoved(trackingParams);
        }
      }

      await syncInsuranceInCart(variables.sku, isInsuranceInCartAfterAction);
    },
    [
      notifyAlertToast,
      syncInsuranceInCart,
      trackInsuranceAdded,
      trackInsuranceRemoved,
      insuranceOffer,
      placement,
    ],
  );

  const { mutate: mutateAdd, isPending: isAddingInsurance } = useMutation({
    ...addInsuranceToCartMutation(),
    onSuccess: (data, variables) =>
      handleInsuranceMutationSuccess(data, variables, true),
    onError: error => {
      notifyAlertToast(getActionErrorMessage(error));
    },
  });

  const { mutate: mutateRemove, isPending: isRemovingInsurance } = useMutation({
    ...removeInsuranceFromCartMutation(),
    onSuccess: (data, variables) =>
      handleInsuranceMutationSuccess(data, variables, false),
    onError: error => {
      notifyAlertToast(getActionErrorMessage(error));
    },
  });

  const addInsurance = useCallback(() => {
    mutateAdd({ sku });
  }, [mutateAdd, sku]);

  const removeInsurance = useCallback(() => {
    mutateRemove({ sku });
  }, [mutateRemove, sku]);

  return {
    insuranceOffer,
    addInsurance,
    removeInsurance,
    isInsuranceActionPending: isAddingInsurance || isRemovingInsurance,
  };
};
